import { useCallback, useEffect, useRef, useState } from 'react';

import {
  btag,
  Bus,
  constants,
  NotificationManager,
  PaymentV2Api,
  pushToDataLayer,
  SessionStorageManager,
  SiteConfigManager,
  useApplicationState,
} from '@atlas/core';
import { clientProfileRoutes, COMPONENT_TYPES, paymentRoutes, staticRoutes } from '@atlas/routing';

import { ACCOUNT_STATUSES, DATA_LAYER_STORAGE_KEYS, POSTHOG_CTA } from 'common/constants';
import {
  buildPaymentUrl,
  showAlternativeMethodsOrError,
} from 'src/common/Popup/alternativePaymentUtils';
import { useBonusesContext } from 'src/components/BonusesProvider';
import { getMinDeposit, isAmountBelowMinDeposit } from 'src/components/BonusesProvider/utils';
import { useSelfRegulationContext } from 'src/components/SelfRegulationProvider';
import { depositSubmit } from 'src/helpers/depositSubmit';

export const useDepositSubmit = ({
  browser,
  closeQRCodeOnSuccess,
  history,
  ip,
  locale,
  onClose,
  onDepositLoaderShown,
  onDepositRedirectSuccess,
  onDepositStart,
  onDepositSuccess,
  openSignupPopup,
  os,
  paymentMethod,
  setPaymentUrl,
  setRedirectUrl,
  showInPopup,
  value,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [depositPopupId, setDepositPopupId] = useState(null);
  const loadingNotificationId = useRef();
  const loaderShownAt = useRef();
  const { selectedBonus } = useBonusesContext();
  const { showSelfRegulationPopupIfNeeded, showSpentLimitPopupIfNeeded } =
    useSelfRegulationContext();
  const {
    account,
    wallet: { currency, detailedBalance },
  } = useApplicationState();
  const { BONUS = 0, CASH = 0, FROZEN = 0 } = detailedBalance || {};
  const balance = BONUS + CASH + FROZEN;

  const clientProfilePath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CLIENT_PROFILE);
  const depositPageUrl = `${clientProfilePath}/${clientProfileRoutes.payment}/${paymentRoutes.deposit}`;

  const onCreateDeposit = useCallback(() => {
    loadingNotificationId.current = NotificationManager.display({
      data: {
        amount: value,
        pmImage: paymentMethod.desktopImageLink,
        pmName: paymentMethod.name,
        text: 'preparing_deposit_text',
        title: 'preparing_deposit_title',
      },
      type: 'depositLoading',
    });
    loaderShownAt.current = Date.now();

    onDepositLoaderShown?.({
      amount: value,
      currency,
      locale,
      method: paymentMethod?.name,
    });
  }, [paymentMethod, value, currency, locale, onDepositLoaderShown]);

  const onDepositStatusError = useCallback(() => {
    Bus.emit('ui:depositErrorHandled');

    NotificationManager.removeNotification(loadingNotificationId.current);

    setDepositPopupId(loadingNotificationId.current);

    const fallbackError = () => {
      NotificationManager.display({
        data: {
          className: 'deposit-failed-popup',
          headerTitle: 'deposit',
          text: 'deposit_failed_message',
          title: 'deposit_failed_title',
        },
        type: 'errorTransaction',
      });
    };

    if (!paymentMethod?.alternativePmEnabled) {
      fallbackError();
      return;
    }

    showAlternativeMethodsOrError({
      fallbackError,
      onSelectMethod: method =>
        history.push(buildPaymentUrl(`${depositPageUrl}/${method.id}`, value, method)),
      onViewAll: () =>
        history.push(buildPaymentUrl(depositPageUrl, value, null), {
          showAllMethods: true,
        }),
      operationType: 'DEPOSIT',
      pmId: paymentMethod?.id,
    });
  }, [depositPageUrl, history, paymentMethod, setDepositPopupId, value]);

  useEffect(() => {
    if (!depositPopupId || !closeQRCodeOnSuccess) return;

    if (balance) {
      const timeoutId = setTimeout(() => {
        NotificationManager.removeNotification(depositPopupId);
        setDepositPopupId(null);
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [balance, closeQRCodeOnSuccess, depositPopupId]);

  const handleDeposit = useCallback(async () => {
    if (account.status === ACCOUNT_STATUSES.PARTIALLY_REGISTERED) {
      if (showInPopup) {
        openSignupPopup();
      } else {
        history.push(staticRoutes.signup);
      }
      return;
    }

    const canProceedSpentLimit = await showSpentLimitPopupIfNeeded();
    if (!canProceedSpentLimit) return;

    const canProceedSelfRegulation = await showSelfRegulationPopupIfNeeded();
    if (!canProceedSelfRegulation) return;

    if (!SessionStorageManager.getItem(DATA_LAYER_STORAGE_KEYS.QUICK_DEPOSIT_BUTTON_CLICKED)) {
      pushToDataLayer(
        'qd_deposit_button_click',
        {
          OS: os,
          amount: value,
          bonus_toggle_enabled: !!selectedBonus,
          browser,
          created_at: Date.now(),
          device: constants.DEVICE_TYPES.MOBILE,
          ip,
          locale,
          page_referer: window.location.pathname,
          payment_method: paymentMethod?.name,
          user_id: account.id,
        },
        btag,
      );
      SessionStorageManager.setItem(DATA_LAYER_STORAGE_KEYS.QUICK_DEPOSIT_BUTTON_CLICKED, true);
    }

    const shouldSendBonus =
      selectedBonus &&
      !selectedBonus.disabled &&
      !isAmountBelowMinDeposit(value, getMinDeposit(selectedBonus, currency));
    const bonusInstanceId = selectedBonus?.id;
    const bonusId = selectedBonus?.bonusId;

    const options = {
      amount: value,
      currency: currency,
      pmId: paymentMethod.id,
      ...(shouldSendBonus && {
        additionalAttributes: {
          bonusId,
          bonusInstanceId,
        },
      }),
    };

    onDepositStart?.({
      amount: value,
      bonus_id: bonusId,
      cta_clicked: POSTHOG_CTA.DEPOSIT,
      payment_method: paymentMethod?.name ?? null,
    });

    setLoading(true);

    PaymentV2Api.getPaymentData({
      onCreatePayment: onCreateDeposit,
      onPaymentStatusError: onDepositStatusError,
      paymentOptions: options,
      paymentType: PaymentV2Api.PaymentType.DEPOSIT,
    })
      .then(data => {
        NotificationManager.removeNotification(loadingNotificationId.current);

        onDepositRedirectSuccess?.({
          amount: value,
          currency,
          locale,
          method: paymentMethod?.name,
          t_wait_ms: loaderShownAt.current ? Date.now() - loaderShownAt.current : null,
        });

        pushToDataLayer(
          'deposit_created',
          {
            amount: value,
            client: {
              id: account.id,
            },
            created_at: Date.now(),
            currency: currency,
            payment_method: data.paymentName,
          },
          btag,
        );

        onDepositSuccess?.({ transaction_id: data.publicPaymentId });

        const popupNotificationId = depositSubmit(
          data,
          paymentMethod,
          setPaymentUrl,
          value,
          setRedirectUrl,
          options,
        );
        setDepositPopupId(popupNotificationId);

        onClose?.();
      })
      .catch(error => {
        const { code } = error;

        if (code) {
          setError({ message: code });
        }
      })
      .finally(() => setLoading(false));
  }, [
    account.id,
    account.status,
    browser,
    currency,
    history,
    ip,
    locale,
    onClose,
    onCreateDeposit,
    onDepositRedirectSuccess,
    onDepositStart,
    onDepositStatusError,
    onDepositSuccess,
    openSignupPopup,
    os,
    paymentMethod,
    selectedBonus,
    setPaymentUrl,
    setRedirectUrl,
    showInPopup,
    showSelfRegulationPopupIfNeeded,
    showSpentLimitPopupIfNeeded,
    value,
  ]);

  return {
    error,
    handleDeposit,
    loading,
    setError,
  };
};
